import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropIcon as Drop } from "@phosphor-icons/react";

const LV = {
  critico: { l: "Critico", c: "bg-red-100 text-red-800 border-red-200" },
  in_esaurimento: { l: "In esaurimento", c: "bg-orange-100 text-orange-800 border-orange-200" },
  esaurimento_previsto: { l: "Esaurimento previsto", c: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  attenzione: { l: "Attenzione", c: "bg-amber-100 text-amber-800 border-amber-200" },
  regolare: { l: "Regolare", c: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  sconosciuto: { l: "Sconosciuto", c: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};
const fmtDt = (d) => (d ? new Date(String(d).replace(" ", "T")).toLocaleDateString("it-IT") : "—");
// Mostra solo i 4 toner principali (nero, ciano, magenta, giallo); nasconde drum/fuser/vaschetta/altro.
const isToner = (r) => r.supply_type === "toner" && ["black", "cyan", "magenta", "yellow"].includes(r.color);

export function PrintanistaSuppliesDashboard({ open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sev, setSev] = useState("all");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/integrations/printanista/supplies").then(({ data }) => setRows(data.supplies || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Errore")).finally(() => setLoading(false));
  }, [open]);

  const toners = rows.filter(isToner);
  const counts = toners.reduce((a, r) => { a[r.active_alert_level] = (a[r.active_alert_level] || 0) + 1; return a; }, {});
  const filtered = toners.filter((r) => (sev === "all" || r.active_alert_level === sev));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="prt-supplies-dashboard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Drop size={18} className="text-orange-600" /> Consumabili</DialogTitle>
          <DialogDescription>Stato consumabili dei dispositivi Printanista collegati.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {["critico", "in_esaurimento", "esaurimento_previsto", "attenzione", "sconosciuto"].map((k) => (
            counts[k] ? <button key={k} onClick={() => setSev(k)} className={`px-2.5 py-1 rounded-sm text-xs border ${LV[k].c}`} data-testid={`prt-supply-banner-${k}`}>{LV[k].l}: {counts[k]}</button> : null
          ))}
        </div>

        <div className="flex gap-2">
          <Select value={sev} onValueChange={setSev}><SelectTrigger className="w-48 rounded-sm h-8" data-testid="prt-supply-filter-sev"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte le gravità</SelectItem>{Object.entries(LV).map(([k, v]) => <SelectItem key={k} value={k}>{v.l}</SelectItem>)}</SelectContent></Select>
        </div>

        {loading ? <div className="py-8 text-center text-sm text-muted-foreground">Caricamento…</div> : (
          <div className="border border-border rounded-sm overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-muted-foreground sticky top-0"><tr className="text-left"><th className="py-2 px-2">Cliente</th><th className="py-2 px-2">Dispositivo</th><th className="py-2 px-2">Consumabile</th><th className="py-2 px-2">Residuo</th><th className="py-2 px-2">Previsione</th><th className="py-2 px-2">Gravità</th></tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={`${r.device_id}-${r.slot_key}`} className="border-t border-border/60" data-testid="prt-supply-dash-row">
                    <td className="py-1.5 px-2 truncate max-w-[140px]">{r.cliente_nome || "—"}</td>
                    <td className="py-1.5 px-2">{[r.marca, r.modello].filter(Boolean).join(" ")}{r.managed === false ? " (non gestito)" : ""}</td>
                    <td className="py-1.5 px-2">{r.supply_name || r.supply_type}{r.color ? ` · ${r.color}` : ""}</td>
                    <td className="py-1.5 px-2 tabular-nums">{r.remaining_value != null ? `${r.remaining_value}${r.remaining_unit === "percent" ? "%" : ""}` : "n/d"}</td>
                    <td className="py-1.5 px-2">{fmtDt(r.estimated_empty_date)}</td>
                    <td className="py-1.5 px-2"><Badge variant="outline" className={`rounded-sm ${LV[r.active_alert_level]?.c || LV.sconosciuto.c}`}>{LV[r.active_alert_level]?.l || "—"}</Badge></td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Nessun consumabile.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
